// @flow
// ----------------------------------------------------------------------
// <copyright file="Izenda.RegressionLineStore.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//  </copyright>
// ----------------------------------------------------------------------

import RegressionLine from 'logic/models/Izenda.RegressionLine';
import { CHART_SETTINGS } from 'logic/data/Izenda.ReportPartChartConstants';
import { get, find, debounce, first } from 'lodash/fp';
import { action, observe, computed, observable } from 'mobx';
import { DEFAULT_DEBOUNCE_TIME } from 'logic/data/Izenda.UIConstants';
import { parser as chartDataParser } from 'share/utils/Izenda.ChartDataParser';

const REGRESSION_LINE_KEY = 'regression';
const { REGRESSION_POLYNOMIAL_ORDER } = CHART_SETTINGS;

export default class RegressionLineStore {
  @observable model: RegressionLine;
  @observable configField: any;
  @observable activeSeriesKey: string;

  serieElements: Array<any>;
  valuesElements: Array<any>;
  valuesLabelsElements: Array<any>;

  /**
   * Order tooltip
   */
  @computed
  get orderTooltip() {
    return get('tooltip', find({ value: this.model.order }, REGRESSION_POLYNOMIAL_ORDER));
  }

  constructor(reportPartDetails: any) {
    this.model = new RegressionLine();

    this.cloneElements(reportPartDetails);
    this.setSerieElements(reportPartDetails);
    this.initData(reportPartDetails);

    const { reportPartContent } = reportPartDetails;
    const activeSerie = reportPartContent.activeSerie;
    if (activeSerie) {
      const currentSerie = find(ele => ele.name === activeSerie.name, this.serieElements);
      this.setConfigField(currentSerie);
    } else {
      this.setConfigField(first(this.serieElements));
    }
  }

  /**
   * Set config field
   */
  @action
  setConfigField(field: any) {
    const regressionSetting = get('regression', field.properties.getOtherProps());
    this.configField = field;
    this.activeSeriesKey = field.name;
    this.model.readData(regressionSetting);
    this.setCurrentSetting();
  }

  /**
   * Clone elements
   */
  @action
  cloneElements(reportPartDetails: any) {
    const content = reportPartDetails.reportPartContent;
    this.valuesElements = content.valuesElements.map(field => field.clone());
    this.valuesLabelsElements = content.valuesLabelsElements.map(field => field.clone());
  }

  /**
   * Set elements in series
   */
  @action
  setSerieElements(reportPartDetails: any) {
    const content = reportPartDetails.reportPartContent;
    this.serieElements = content.getMetricContainer() === 'values' ? this.valuesElements : this.valuesLabelsElements;
  }

  /**
   * set current setting
   */
  setCurrentSetting() {
    const {
      name,
      visible,
      hideInLegend,
      type,
      order,
      lineWidth,
      dashStyle,
      color,
      extrapolate,
      decimalPlaces
    } = this.model;

    this.configField.setOtherProps(REGRESSION_LINE_KEY, {
      name,
      visible,
      hideInLegend,
      type,
      order,
      lineWidth,
      dashStyle,
      color,
      extrapolate,
      decimalPlaces
    });
  }

  /**
   * Init data
   * Store drilldown data to this instance
   */
  initData(reportPartDetails: any) {
    this.setPreviewData(reportPartDetails);
    this.dataObserverDisposer = observe(reportPartDetails, 'isLoadingData', () =>
      this.setPreviewData(reportPartDetails)
    );
  }

  /**
   * Set preview data
   */
  setPreviewData(reportPartDetails: any) {
    const reportPartContent = reportPartDetails.reportPartContent;
    this.expandedLevel = reportPartDetails.reportPartContent.expandedLevel;
    this.previewData = reportPartDetails.previewData;
    this.previewDataRecords = reportPartDetails.previewDataRecords || [];
    this.previewDataFieldsMapping = reportPartDetails.previewDataFieldsMapping || [];
    this.activeSeriesKey = reportPartContent.getActiveSerieKey();
  }

  /**
   * Dispose data observer
   */
  disposeDataObserver() {
    this.dataObserverDisposer && this.dataObserverDisposer();
  }

  /**
   * Update value to configuration
   * @param {string} changedKey
   * @param {any} field
   * @param {any} schemaData
   */
  updateValue(changedKey, field, schemaData) {
    this.model.updateValue(changedKey, schemaData[changedKey].value);
    this.setCurrentSetting();
  }

  /**
   * Change name in debounce
   */
  changeName = debounce(
    DEFAULT_DEBOUNCE_TIME,
    action((field, schemaData) => {
      this.updateValue('name', field, schemaData);
    })
  );

  /**
   * Change decimal places in debounce
   */
  changeDecimalPlaces = debounce(
    DEFAULT_DEBOUNCE_TIME,
    action((field, schemaData) => {
      this.updateValue('decimalPlaces', field, schemaData);
    })
  );

  /**
   * Change extrapolate in debounce
   */
  changeExtrapolate = debounce(
    DEFAULT_DEBOUNCE_TIME,
    action((field, schemaData) => {
      this.updateValue('extrapolate', field, schemaData);
    })
  );

  /**
   * Update element when saved
   * @param {Object} reportPartContent
   */
  updateElements(reportPartContent: any) {
    const updateElements =
      reportPartContent.getMetricContainer() === 'values'
        ? reportPartContent.valuesElements
        : reportPartContent.valuesLabelsElements;

    updateElements.forEach((item, index) => {
      item.setOtherProps(
        REGRESSION_LINE_KEY,
        this.serieElements[index].properties.getOtherProps()[REGRESSION_LINE_KEY]
      );
    });
  }

  /**
   * Build field options
   */
  buildFieldOptions() {
    return this.serieElements.map(({ name }) => ({ value: name, label: name }));
  }

  /**
   * Get chart data
   */
  getChartData(reportPartContent: any) {
    return chartDataParser(reportPartContent.getActiveElmsByActiveSeries(this.activeSeriesKey), this.expandedLevel, {
      fieldsMapping: this.previewDataFieldsMapping,
      records: this.previewDataRecords,
      rawData: this.previewData
    });
  }
}



// WEBPACK FOOTER //
// ./ui/logic/Izenda.RegressionLineStore.js